/**
 * Audit Event Types and Interfaces
 * Types for the observability audit trail system
 */

export interface AuditEvent {
  id: number;
  timestamp: string;
  eventType: string;
  category: string;
  severity: 'Info' | 'Warning' | 'Error' | 'Critical';
  actorId?: string;
  actorEmail?: string;
  actorRole?: string;
  targetEntityType?: string;
  targetEntityId?: string;
  action: string;
  description: string;
  oldValues?: string;
  newValues?: string;
  correlationId?: string;
  ipAddress?: string;
  userAgent?: string;
  endpoint?: string;
  httpMethod?: string;
  metadata?: string;
  integrityHash?: string;
}

export interface AuditQueryParams {
  page?: number;
  pageSize?: number;
  eventType?: string;
  category?: string;
  severity?: string;
  actorId?: string;
  fromDate?: string;
  toDate?: string;
  searchTerm?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

export interface PagedAuditResult {
  items: AuditEvent[];
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface AuditSummary {
  totalEvents: number;
  eventsByCategory: Record<string, number>;
  eventsBySeverity: Record<string, number>;
  topActors: ActorActivity[];
  eventTypeBreakdown: EventTypeCount[];
  hourlyDistribution: TimeSeriesDataPoint[];
}

export interface ActorActivity {
  actorId: string;
  actorEmail?: string;
  eventCount: number;
  lastActivity: string;
}

export interface EventTypeCount {
  eventType: string;
  count: number;
  percentage: number;
}

export interface TimeSeriesDataPoint {
  timestamp: string;
  count: number;
}

export interface TransactionTrace {
  correlationId: string;
  startTime: string;
  endTime?: string;
  durationMs?: number;
  events: AuditEvent[];
  eventCount: number;
}

export interface AuditQueueStatus {
  pendingCount: number;
  maxCapacity: number;
  utilizationPercentage: number;
  lastFlushTime?: string;
  isHealthy: boolean;
}

// Event type constants for filtering
export const AUDIT_CATEGORIES = [
  'Authentication',
  'Booking',
  'Payment',
  'Subscription',
  'Profile',
  'Admin',
  'System',
  'Security',
] as const;

export const AUDIT_SEVERITIES = ['Info', 'Warning', 'Error', 'Critical'] as const;

export type AuditCategory = (typeof AUDIT_CATEGORIES)[number];
export type AuditSeverity = (typeof AUDIT_SEVERITIES)[number];

// Severity badge colors
export const SEVERITY_COLORS: Record<AuditSeverity, { bg: string; text: string }> = {
  Info: { bg: 'bg-blue-100', text: 'text-blue-800' },
  Warning: { bg: 'bg-yellow-100', text: 'text-yellow-800' },
  Error: { bg: 'bg-red-100', text: 'text-red-800' },
  Critical: { bg: 'bg-red-200', text: 'text-red-900' },
};

// Category icons (using emoji as placeholders - can be replaced with icon components)
export const CATEGORY_ICONS: Record<string, string> = {
  Authentication: '🔐',
  Booking: '📅',
  Payment: '💳',
  Subscription: '📦',
  Profile: '👤',
  Admin: '⚙️',
  System: '🖥️',
  Security: '🛡️',
};
